import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowRight, Ship, BarChart3, AlertTriangle, Receipt, Award, Bot, ShieldCheck, Loader2 } from "lucide-react";

const heroBg = "https://static.prod-images.emergentagent.com/jobs/98b5b9c5-b160-4225-851a-f4b136e1c736/images/00d0349cf3949d4da8a063ff102dd1a542389b722fcdba72b43e9d76cbfcaee8.png";
const conceptImg = "https://static.prod-images.emergentagent.com/jobs/98b5b9c5-b160-4225-851a-f4b136e1c736/images/a778cfdf3c204d2ebcef930cfa62ca439966371e7e85f75c0cf6d5abe385ad95.png";
const shipImg = "https://images.pexels.com/photos/27402391/pexels-photo-27402391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PHASES = [
  {
    tag: "BEFORE",
    title: "Procure freight with intelligence.",
    body: "Benchmark rates against the live market, compare carriers on cost, transit and reliability, model BAF/PSS surcharges before you commit, and plan bookings with PO readiness.",
    bullets: ["Lane rate benchmark", "Surcharge calculator", "Booking control", "RFQ-ready insights"],
  },
  {
    tag: "DURING",
    title: "See every container in motion.",
    body: "Predict ETA, classify exceptions, manage cut-offs and documents. Know what needs action today — not what already failed.",
    bullets: ["Predictive ETA", "Exception dashboard", "Free-time countdown", "Document compliance"],
  },
  {
    tag: "AFTER",
    title: "Recover spend you already lost.",
    body: "Audit every freight invoice line-by-line, generate detention waivers in seconds, score carriers, and turn data into your next QBR.",
    bullets: ["Quote vs invoice match", "Detention dispute pack", "Carrier scorecard", "Spend &amp; savings analytics"],
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [lead, setLead] = useState({ company_name: "", contact_name: "", contact_email: "", teu_per_year: "", message: "" });

  const startCheckout = async (planId) => {
    setCheckoutBusy(planId);
    try {
      const r = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout");
        setCheckoutBusy(null);
      }
    } catch (e) {
      toast.error("Checkout failed. Please retry.");
      setCheckoutBusy(null);
    }
  };

  const submitLead = async () => {
    if (!lead.company_name || !lead.contact_email) {
      toast.error("Company and email are required");
      return;
    }
    try {
      await api.post("/billing/enterprise-lead", {
        ...lead,
        teu_per_year: lead.teu_per_year ? Number(lead.teu_per_year) : null,
      });
      toast.success("Thanks — our team will be in touch within 1 business day.");
      setEnterpriseOpen(false);
      setLead({ company_name: "", contact_name: "", contact_email: "", teu_per_year: "", message: "" });
    } catch {
      toast.error("Could not submit. Try again.");
    }
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
      {/* Top nav */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-mono text-[11px] font-bold tracking-tighter">CT</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading font-black text-[17px] tracking-tight">WEDELVR</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Freight Control Tower</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 mx-auto text-sm text-zinc-700">
            <a href="#platform" className="hover:text-[#002FA7]">Platform</a>
            <a href="#modules" className="hover:text-[#002FA7]">Modules</a>
            <a href="#pricing" className="hover:text-[#002FA7]">Pricing</a>
          </nav>
          <Button
            data-testid="landing-enter-app"
            className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white"
            onClick={() => navigate('/dashboard')}
          >
            Enter Control Tower <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div
          className="absolute inset-0 opacity-90"
          style={{ backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="data-label text-[#002FA7] mb-5">A subscription control tower for importers &amp; exporters</div>
            <h1 className="font-heading text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-6">
              Control freight cost <span className="text-[#002FA7]">before, during and after</span> every shipment.
            </h1>
            <p className="text-lg text-zinc-700 leading-relaxed max-w-xl mb-8">
              WeDelvr replaces the spreadsheets, email chains, and forwarder portals with one decision-support
              platform — for procurement, visibility, detention, invoice audit and carrier performance.
            </p>
            <div className="flex gap-3">
              <Button
                data-testid="hero-cta-primary"
                size="lg"
                className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white text-base px-6 h-12"
                onClick={() => navigate('/dashboard')}
              >
                See your Control Tower <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                size="lg"
                variant="outline"
                className="rounded-none border-zinc-300 text-base px-6 h-12 hover:bg-white"
                onClick={() => navigate('/consultant')}
              >
                Ask the AI Consultant
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 max-w-lg gap-px bg-zinc-200 border border-zinc-200">
              {[
                { v: "12-18%", l: "Avg freight savings" },
                { v: "$2.4K", l: "Avoidable D&D / container" },
                { v: "1 view", l: "All forwarders, one dashboard" },
              ].map((s) => (
                <div key={s.l} className="bg-white p-4">
                  <div className="metric text-2xl font-medium tracking-tight">{s.v}</div>
                  <div className="text-[11px] text-zinc-500 uppercase tracking-[0.18em] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="bg-white border border-zinc-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="data-label">Live Tower · DEMO</div>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#16A34A]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse-dot" /> Online
                </span>
              </div>
              <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
                {[
                  { k: "Open Bookings", v: "24", a: "border-l-2 border-l-[#002FA7]" },
                  { k: "In Transit", v: "12", a: "" },
                  { k: "At Risk", v: "6", a: "border-l-2 border-l-[#DC2626]" },
                  { k: "LFD ≤ 3 days", v: "4", a: "border-l-2 border-l-[#F59E0B]" },
                ].map((t) => (
                  <div key={t.k} className={`bg-white p-4 ${t.a}`}>
                    <div className="data-label mb-2">{t.k}</div>
                    <div className="metric text-3xl">{t.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-zinc-200 pt-4 text-xs">
                <div className="flex items-center justify-between mb-2 text-zinc-600">
                  <span>SHP-10003 · Shanghai → Sydney</span>
                  <span className="metric text-[#B45309]">+5d delayed</span>
                </div>
                <div className="flex items-center justify-between mb-2 text-zinc-600">
                  <span>INV-70014 · Overcharge flagged</span>
                  <span className="metric text-[#991B1B]">+ $612</span>
                </div>
                <div className="flex items-center justify-between text-zinc-600">
                  <span>MSKU2238441 · LFD in 2d</span>
                  <span className="metric text-[#B45309]">Warning</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three phases */}
      <section id="platform" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="mb-12 max-w-2xl">
          <div className="data-label text-[#002FA7] mb-3">The WeDelvr Method</div>
          <h2 className="font-heading text-4xl font-bold tracking-tight mb-4">One platform. Three control points.</h2>
          <p className="text-zinc-600 text-base leading-relaxed">
            Customers don't lose money on freight only when shipments are late. They lose it before — by paying the wrong rate.
            And after — by missing invoice errors. WeDelvr covers all three.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {PHASES.map((p, idx) => (
            <div key={p.tag} className="bg-white p-8">
              <div className="flex items-baseline gap-3 mb-5">
                <span className="metric text-[11px] text-zinc-400">0{idx + 1}</span>
                <span className="data-label text-[#002FA7]">{p.tag} SHIPMENT</span>
              </div>
              <h3 className="font-heading text-2xl font-bold mb-3 tracking-tight">{p.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed mb-5">{p.body}</p>
              <ul className="space-y-1.5 text-sm">
                {p.bullets.map(b => (
                  <li key={b} className="flex items-center gap-2 text-zinc-700">
                    <span className="w-1.5 h-1.5 bg-[#002FA7]" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Modules grid */}
      <section id="modules" className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="data-label text-[#002FA7] mb-3">Modules</div>
              <h2 className="font-heading text-4xl font-bold tracking-tight mb-4">Every workflow your team already runs in spreadsheets.</h2>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Built for Australian importers moving 50–1,000 TEU per year — and their suppliers, brokers, and forwarders.
                Single source of truth, structured data, automated alerts.
              </p>
              <Button
                data-testid="modules-cta"
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"
                onClick={() => navigate('/dashboard')}
              >
                Explore the platform <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
                {[
                  { i: BarChart3, t: "Procurement", d: "Rate benchmark, surcharge calc, RFQ insights" },
                  { i: Ship, t: "Visibility", d: "Container tracking, predictive ETA, exceptions" },
                  { i: AlertTriangle, t: "Detention & Demurrage", d: "Free-time alerts, waiver/dispute generator" },
                  { i: Receipt, t: "Invoice Audit", d: "Line-by-line, surcharge validation, savings tracker" },
                  { i: Award, t: "Carrier Scorecard", d: "Reliability, transit, rollover, invoice accuracy" },
                  { i: Bot, t: "AI Consultant", d: "Claude 4.5 &amp; GPT-5.2 procurement advisor" },
                ].map(m => {
                  const Icon = m.i;
                  return (
                    <div key={m.t} className="bg-white p-5">
                      <Icon className="w-5 h-5 text-[#002FA7] mb-3" />
                      <div className="font-heading font-semibold text-base mb-1">{m.t}</div>
                      <div className="text-xs text-zinc-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: m.d }} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strip with hero image */}
      <section className="border-t border-zinc-200">
        <div className="grid lg:grid-cols-2">
          <div className="bg-white p-12 lg:p-16 flex flex-col justify-center">
            <div className="data-label text-[#002FA7] mb-4">AI SUPPLY CHAIN CONSULTANT</div>
            <h2 className="font-heading text-4xl font-bold tracking-tight mb-4">A senior procurement advisor — on demand.</h2>
            <p className="text-zinc-600 leading-relaxed mb-6 max-w-lg">
              "Am I paying the right rate?" "Should I lock contract or stay spot?" "How do I avoid detention on this shipment?"
              Get expert answers in seconds, powered by your data and frontier LLMs.
            </p>
            <Button
              data-testid="consultant-cta"
              variant="outline"
              className="rounded-none border-zinc-900 hover:bg-zinc-900 hover:text-white w-fit"
              onClick={() => navigate('/consultant')}
            >
              Ask the consultant <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div
            className="min-h-[320px] bg-zinc-100"
            style={{ backgroundImage: `url(${conceptImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          />
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="data-label text-[#002FA7] mb-3">Subscription Plans</div>
          <h2 className="font-heading text-4xl font-bold tracking-tight mb-4">Visibility + freight cost control. Not just tracking.</h2>
          <p className="text-sm text-zinc-600 max-w-2xl mb-8">Simple monthly plans with a small per-shipment fee. Cancel anytime. No lock-in. Launch-priced for the first 100 customers.</p>

          {/* Foundation offer banner */}
          <div className="bg-[#FEF3C7] border border-[#FDE68A] p-4 mb-10 flex items-start gap-3" data-testid="foundation-offer">
            <ShieldCheck className="w-5 h-5 text-[#92400E] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-[#92400E] font-semibold mb-1">Foundation Customer Offer</div>
              <div className="text-sm text-[#78350F]">First 3 months at <b>50% off</b> the monthly subscription. No lock-in. Cancel anytime. Includes shipment visibility, freight cost review, invoice checks and a monthly savings report.</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {[
              {
                name: "Starter", plan_id: "starter",
                price: "$0", per: "/month", per_ship: "$5/shipment",
                who: "Trial users / small importers",
                feats: [
                  "Container / MBL tracking",
                  "Basic dashboard",
                  "ETA updates",
                  "Email alerts",
                  "Up to 10 shipments/month",
                  "Email support",
                ],
              },
              {
                name: "Control", plan_id: "control",
                price: "$199", per: "/month", per_ship: "$10/shipment",
                who: "SMEs — 10 to 50 shipments/month",
                launch_price: "$99/mo for first 3 months",
                feats: [
                  "Everything in Starter",
                  "PO &amp; order tracking",
                  "Supplier shipment dashboard",
                  "Carrier schedule visibility",
                  "Freight cost review",
                  "Monthly savings report",
                  "Basic invoice checking",
                  "Exception alerts",
                  "Priority email + WhatsApp support",
                ],
                featured: true,
              },
              {
                name: "Premium Advisory", plan_id: "premium",
                price: "$499", per: "/month", per_ship: "$15/shipment",
                who: "Customers who want expert support",
                launch_price: "$249/mo for first 3 months",
                feats: [
                  "Everything in Control",
                  "Freight rate benchmarking",
                  "Carrier/forwarder quote comparison",
                  "Monthly freight spend review",
                  "Detention &amp; demurrage risk alerts",
                  "Invoice audit support",
                  "Savings opportunity report",
                  "1-hour monthly consulting call",
                  "Light tender/procurement support",
                ],
              },
              {
                name: "Enterprise", plan_id: "enterprise",
                price: "Custom", per: "from $1.5k/mo",
                who: "100+ shipments/month or annual tenders",
                feats: [
                  "Everything in Premium Advisory",
                  "Freight tender management",
                  "Carrier/forwarder negotiation support",
                  "Contract review",
                  "Board-style monthly supply chain report",
                  "Quarterly business review",
                  "API / ERP integration",
                  "Dedicated account manager",
                  "Optional 10–20% success fee on verified savings",
                ],
              },
            ].map(p => (
              <div key={p.name} className={`bg-white p-7 flex flex-col ${p.featured ? 'border-t-2 border-t-[#002FA7]' : ''}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                  {p.featured && <span className="text-[10px] uppercase tracking-[0.15em] text-[#002FA7] bg-[#E6EBF6] px-2 py-0.5">Recommended</span>}
                </div>
                <p className="text-xs text-zinc-500 mb-4">{p.who}</p>
                <div className="mb-2">
                  <span className="metric text-4xl font-medium">{p.price}</span>
                  <span className="text-sm text-zinc-500 ml-1">{p.per}</span>
                </div>
                {p.per_ship && <div className="text-xs text-zinc-500 metric mb-2">+ {p.per_ship}</div>}
                {p.launch_price && (
                  <div className="text-[11px] text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] px-2 py-1 mb-4 metric">{p.launch_price}</div>
                )}
                <ul className="space-y-2 text-sm mb-6 flex-grow">
                  {p.feats.map(f => (
                    <li key={f} className="flex gap-2 text-zinc-700">
                      <ShieldCheck className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                      <span className="text-[13px] leading-snug" dangerouslySetInnerHTML={{ __html: f }} />
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`pricing-cta-${p.plan_id}`}
                  className={`rounded-none w-full ${p.featured ? 'bg-[#002FA7] hover:bg-[#002480] text-white' : 'bg-zinc-900 hover:bg-zinc-800 text-white'}`}
                  disabled={checkoutBusy === p.plan_id}
                  onClick={() => {
                    if (p.plan_id === "enterprise") setEnterpriseOpen(true);
                    else if (p.plan_id === "starter") startCheckout(p.plan_id);
                    else startCheckout(p.plan_id);
                  }}
                >
                  {checkoutBusy === p.plan_id
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting...</>
                    : p.plan_id === "enterprise" ? "Talk to sales"
                    : p.plan_id === "starter" ? "Start free"
                    : "Start subscription"}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-6 text-xs text-zinc-500 metric">
            * Per-shipment fees are invoiced monthly in arrears based on actual usage. Foundation discount applies to monthly subscription only.
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-zinc-900 text-zinc-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-mono text-[11px] font-bold tracking-tighter">CT</span>
            </div>
            <span className="font-heading font-bold text-white tracking-tight">WEDELVR</span>
          </div>
          <div className="text-xs text-zinc-500">© 2026 WeDelvr. Built for global freight teams.</div>
        </div>
      </footer>

      {/* Enterprise contact dialog */}
      <Dialog open={enterpriseOpen} onOpenChange={setEnterpriseOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="enterprise-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Talk to Enterprise Sales</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">For 1,000+ TEU/year operations. We typically respond within 1 business day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="data-label">Company</Label><Input data-testid="ent-company" value={lead.company_name} onChange={e => setLead({ ...lead, company_name: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="data-label">Your Name</Label><Input data-testid="ent-name" value={lead.contact_name} onChange={e => setLead({ ...lead, contact_name: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label className="data-label">Email</Label><Input data-testid="ent-email" type="email" value={lead.contact_email} onChange={e => setLead({ ...lead, contact_email: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label className="data-label">Annual TEU Volume</Label><Input data-testid="ent-teu" type="number" value={lead.teu_per_year} onChange={e => setLead({ ...lead, teu_per_year: e.target.value })} className="rounded-none mt-1 metric" /></div>
            <div><Label className="data-label">Tell us about your operation</Label><Textarea data-testid="ent-msg" value={lead.message} onChange={e => setLead({ ...lead, message: e.target.value })} className="rounded-none mt-1" rows={3} /></div>
            <Button data-testid="ent-submit" onClick={submitLead} className="rounded-none bg-[#002FA7] hover:bg-[#002480] w-full">Send request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
